import api from "./api"
import { RegistrarLogErro } from "./clientefiel"

export interface ExportacaoCardapio {
    conteudo: string
    nomeArquivo: string
}

export interface ImportacaoCardapioResultado {
    mensagem: string
    idCardapio: number
}

export interface ErroApi {
    retornoErro: number
    mensagem: string
}

export type SeparadorExportacao = "ponto-virgula" | "tab"

type RespostaErroRaw = { codErro: number; mensagem?: string }

function isErroBackend(data: unknown): data is RespostaErroRaw {
    return (
        typeof data === "object" &&
        data !== null &&
        !Array.isArray(data) &&
        "codErro" in data &&
        (data as RespostaErroRaw).codErro > 0
    )
}

function erroParaRespostaRaw(error: unknown): RespostaErroRaw {
    const axiosError = error as { message?: string; response?: { data?: unknown } }
    const data = axiosError.response?.data
    if (isErroBackend(data)) {
        return { codErro: data.codErro, mensagem: data.mensagem }
    }
    if (axiosError.message === "Network Error") {
        return { codErro: 1, mensagem: "Verifique sua conexão!" }
    }
    return { codErro: 1, mensagem: axiosError.message || "Erro desconhecido" }
}

export const exportarCardapio = async function (
    authorization: string,
    idEstabelecimento: number,
    separador: SeparadorExportacao,
    agrupamento: boolean
): Promise<ExportacaoCardapio | ErroApi> {
    try {
        const response = await api
            .get<ExportacaoCardapio | RespostaErroRaw>(`cardapio/exportar/${idEstabelecimento}`, {
                params: {
                    separador: separador === "tab" ? "tab" : "ponto-virgula",
                    agrupamento,
                },
                headers: {
                    "Content-Type": "application/json",
                    Authorization: authorization,
                },
            })
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })

        if (isErroBackend(response.data)) {
            return { retornoErro: response.data.codErro, mensagem: response.data.mensagem ?? "" }
        }
        return response.data as ExportacaoCardapio
    } catch (error) {
        RegistrarLogErro(error, "exportarCardapio", String(idEstabelecimento))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export interface ProgressoImportacao {
    /** 0 a 100 */
    percentual: number
    etapa: string
}

type StatusImportacaoRaw = {
    idImportacao: string
    status: "PROCESSANDO" | "CONCLUIDO" | "ERRO"
    percentual: number
    etapa?: string
    mensagem?: string
    resultado?: ImportacaoCardapioResultado
}

const INTERVALO_CONSULTA_MS = 1500
// Falhas seguidas na consulta do andamento (rede instável, deploy) antes de desistir.
// A importação continua no servidor mesmo que o painel pare de acompanhar.
const MAX_FALHAS_CONSULTA = 20

const esperar = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

const headersJson = (authorization: string) => ({
    "Content-Type": "application/json",
    Authorization: authorization,
})

// Fluxo antigo, síncrono: uma única requisição que só responde ao fim da importação.
// Usado apenas como fallback quando o backend ainda não tem o endpoint assíncrono.
async function importarCardapioSincrono(
    authorization: string,
    idEstabelecimento: number,
    conteudo: string,
    fotosEmPng: boolean
): Promise<ImportacaoCardapioResultado | ErroApi> {
    const response = await api
        .post<ImportacaoCardapioResultado | RespostaErroRaw>(
            `cardapio/importar/${idEstabelecimento}`,
            { conteudo, fotosEmPng },
            { headers: headersJson(authorization), timeout: 0 }
        )
        .catch(function (error) {
            return { data: erroParaRespostaRaw(error) }
        })

    if (isErroBackend(response.data)) {
        return { retornoErro: response.data.codErro, mensagem: response.data.mensagem ?? "" }
    }
    return response.data as ImportacaoCardapioResultado
}

// Importa o cardápio em segundo plano no backend: a requisição inicial responde na hora
// com um idImportacao e o andamento é consultado até concluir. Assim cardápios grandes
// (muitas linhas/fotos) não estouram o timeout do servidor, e o painel mostra o progresso.
export const importarCardapio = async function (
    authorization: string,
    idEstabelecimento: number,
    conteudo: string,
    fotosEmPng: boolean,
    onProgresso?: (progresso: ProgressoImportacao) => void
): Promise<ImportacaoCardapioResultado | ErroApi> {
    try {
        onProgresso?.({ percentual: 0, etapa: "Enviando o conteúdo" })
        let endpointInexistente = false
        const inicio = await api
            .post<StatusImportacaoRaw | RespostaErroRaw>(
                `cardapio/importar-assincrono/${idEstabelecimento}`,
                { conteudo, fotosEmPng },
                { headers: headersJson(authorization), timeout: 0 }
            )
            .catch(function (error) {
                if (error?.response?.status === 404 || error?.response?.status === 405) {
                    endpointInexistente = true
                }
                return { data: erroParaRespostaRaw(error) }
            })

        if (endpointInexistente) {
            onProgresso?.({ percentual: 0, etapa: "Importando (aguarde, pode levar alguns minutos)" })
            return await importarCardapioSincrono(
                authorization,
                idEstabelecimento,
                conteudo,
                fotosEmPng
            )
        }
        if (isErroBackend(inicio.data)) {
            return { retornoErro: inicio.data.codErro, mensagem: inicio.data.mensagem ?? "" }
        }

        const idImportacao = (inicio.data as StatusImportacaoRaw).idImportacao
        let falhasSeguidas = 0
        for (;;) {
            await esperar(INTERVALO_CONSULTA_MS)
            const resp = await api
                .get<StatusImportacaoRaw | RespostaErroRaw>(
                    `cardapio/importar-status/${idImportacao}`,
                    { headers: headersJson(authorization) }
                )
                .catch(function (error) {
                    return { data: erroParaRespostaRaw(error), falhaRede: !error?.response }
                })

            if ("falhaRede" in resp && resp.falhaRede) {
                falhasSeguidas++
                if (falhasSeguidas >= MAX_FALHAS_CONSULTA) {
                    return {
                        retornoErro: 1,
                        mensagem:
                            "Não foi possível acompanhar a importação (verifique sua conexão). Ela continua no servidor: confira o cardápio em alguns minutos.",
                    }
                }
                continue
            }
            falhasSeguidas = 0

            if (isErroBackend(resp.data)) {
                return { retornoErro: resp.data.codErro, mensagem: resp.data.mensagem ?? "" }
            }
            const status = resp.data as StatusImportacaoRaw
            onProgresso?.({
                percentual: Math.max(0, Math.min(100, status.percentual ?? 0)),
                etapa: status.etapa ?? "",
            })
            if (status.status === "CONCLUIDO") {
                return (
                    status.resultado ?? {
                        mensagem: status.mensagem ?? "Importação realizada com sucesso!",
                        idCardapio: 0,
                    }
                )
            }
            if (status.status === "ERRO") {
                return { retornoErro: 1, mensagem: status.mensagem ?? "Erro ao importar o cardápio" }
            }
        }
    } catch (error) {
        RegistrarLogErro(error, "importarCardapio", String(idEstabelecimento))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}
